import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { api } from "@/lib/api";
import { ImageUpload } from "@/components/admin/ImageUpload";

const Field = ({ label, value, onChange, textarea, testid, placeholder }) => (
  <div>
    <label className="text-white/60 text-xs uppercase tracking-widest block mb-2">{label}</label>
    {textarea ? (
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testid} placeholder={placeholder}
        className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#FF1A1A] outline-none py-2.5 px-3 text-white text-sm transition-colors resize-none placeholder:text-white/25" />
    ) : (
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
        className="w-full bg-[#0a0a0a] border border-white/15 focus:border-[#FF1A1A] outline-none py-2.5 px-3 text-white text-sm transition-colors placeholder:text-white/25" />
    )}
  </div>
);

const Section = ({ title, children }) => (
  <div className="bg-[#111] border border-white/10 p-6 mb-5">
    <h3 className="font-heading text-lg font-bold text-white mb-5">{title}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
  </div>
);

export const SettingsManager = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((res) => setS(res.data || {})).catch(() => setS({}));
  }, []);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const setItem = (field, index, key, value) => {
    setS((p) => {
      const arr = [...(p[field] || [])];
      arr[index] = { ...arr[index], [key]: value };
      return { ...p, [field]: arr };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...s };
      if (typeof payload.work_areas === "string") {
        payload.work_areas = payload.work_areas.split(",").map((x) => x.trim()).filter(Boolean);
      }
      await api.put("/admin/settings", payload);
      toast.success("Instellingen opgeslagen");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <div className="text-white/40 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Laden...</div>;

  const areasValue = Array.isArray(s.work_areas) ? s.work_areas.join(", ") : (s.work_areas || "");

  return (
    <div data-testid="settings-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-bold text-white">Instellingen</h2>
        <button onClick={save} disabled={saving} data-testid="save-settings-button" className="bg-[#FF1A1A] text-white px-6 py-2.5 font-semibold flex items-center gap-2 hover:bg-[#E60000] transition-colors disabled:opacity-60">
          {saving ? <><Loader2 size={17} className="animate-spin" /> Opslaan...</> : <><Save size={17} /> Opslaan</>}
        </button>
      </div>

      <Section title="Hero (bovenaan de pagina)">
        <Field label="Titel regel 1" value={s.hero_title_line1} onChange={(v) => set("hero_title_line1", v)} testid="set-hero1" />
        <Field label="Titel regel 2 (rood)" value={s.hero_title_line2} onChange={(v) => set("hero_title_line2", v)} testid="set-hero2" />
        <div className="md:col-span-2"><Field label="Subtekst" value={s.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} textarea testid="set-herosub" /></div>
      </Section>

      <Section title="Over ons">
        <div className="md:col-span-2"><Field label="Kop" value={s.about_heading} onChange={(v) => set("about_heading", v)} testid="set-abouthead" /></div>
        <div className="md:col-span-2"><Field label="Tekst" value={s.about_text} onChange={(v) => set("about_text", v)} textarea testid="set-abouttext" /></div>
        <div className="md:col-span-2"><ImageUpload label="Afbeelding (rechts)" value={s.about_image} onChange={(v) => set("about_image", v)} testid="set-aboutimg-upload" /></div>
      </Section>

      <Section title="Logo">
        <div className="md:col-span-2"><ImageUpload label="Logo afbeelding (transparant PNG/WEBP aanbevolen)" value={s.logo_url} onChange={(v) => set("logo_url", v)} testid="set-logo-upload" /></div>
        <Field label="Logo grootte navigatie (px)" value={s.nav_logo_height} onChange={(v) => set("nav_logo_height", Number(v) || 0)} testid="set-navlogo" placeholder="48" />
        <Field label="Logo grootte hero/bovenaan (px)" value={s.hero_logo_height} onChange={(v) => set("hero_logo_height", Number(v) || 0)} testid="set-herologo" placeholder="200" />
        <Field label="Logo grootte footer (px)" value={s.footer_logo_height} onChange={(v) => set("footer_logo_height", Number(v) || 0)} testid="set-footerlogo" placeholder="112" />
      </Section>

      <Section title="Oproep-blok (CTA)">
        <div className="md:col-span-2"><Field label="Titel" value={s.cta_title} onChange={(v) => set("cta_title", v)} testid="set-ctatitle" /></div>
        <div className="md:col-span-2"><Field label="Tekst" value={s.cta_text} onChange={(v) => set("cta_text", v)} textarea testid="set-ctatext" /></div>
      </Section>

      <Section title="Bedrijfsgegevens">
        <Field label="Telefoon (weergave)" value={s.phone} onChange={(v) => set("phone", v)} testid="set-phone" placeholder="06 53 42 29 01" />
        <Field label="Telefoon (bel-link, +31...)" value={s.phoneRaw} onChange={(v) => set("phoneRaw", v)} testid="set-phoneraw" placeholder="+31653422901" />
        <Field label="WhatsApp nummer (31...)" value={s.whatsapp} onChange={(v) => set("whatsapp", v)} testid="set-whatsapp" placeholder="31653422901" />
        <Field label="E-mail" value={s.email} onChange={(v) => set("email", v)} testid="set-email" />
        <Field label="Locatie" value={s.location} onChange={(v) => set("location", v)} testid="set-location" />
        <Field label="Website" value={s.website} onChange={(v) => set("website", v)} testid="set-website" />
        <Field label="KvK-nummer" value={s.kvk} onChange={(v) => set("kvk", v)} testid="set-kvk" />
        <Field label="BTW-nummer" value={s.btw} onChange={(v) => set("btw", v)} testid="set-btw" />
      </Section>

      <Section title="Social media">
        <Field label="Instagram URL" value={s.instagram} onChange={(v) => set("instagram", v)} testid="set-instagram" />
        <Field label="Facebook URL" value={s.facebook} onChange={(v) => set("facebook", v)} testid="set-facebook" />
        <Field label="TikTok URL" value={s.tiktok} onChange={(v) => set("tiktok", v)} testid="set-tiktok" />
        <Field label="Google URL" value={s.google} onChange={(v) => set("google", v)} testid="set-google" />
      </Section>

      <Section title="Werkgebied">
        <div className="md:col-span-2"><Field label="Plaatsen (gescheiden door komma's)" value={areasValue} onChange={(v) => set("work_areas", v)} textarea testid="set-areas" placeholder="Arnhem, Huissen, Nijmegen" /></div>
      </Section>

      <div className="bg-[#111] border border-white/10 p-6 mb-5">
        <h3 className="font-heading text-lg font-bold text-white mb-5">Waarom Klusbedrijf Pruijn (4 blokken)</h3>
        <div className="flex flex-col gap-5">
          {(s.why_us || []).map((b, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-5 border-b border-white/5 last:border-0">
              <Field label={`Blok ${i + 1} — titel`} value={b.title} onChange={(v) => setItem("why_us", i, "title", v)} testid={`set-why-title-${i}`} />
              <Field label={`Blok ${i + 1} — tekst`} value={b.text} onChange={(v) => setItem("why_us", i, "text", v)} testid={`set-why-text-${i}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#111] border border-white/10 p-6 mb-5">
        <h3 className="font-heading text-lg font-bold text-white mb-5">Werkwijze (stappen)</h3>
        <div className="flex flex-col gap-5">
          {(s.process || []).map((p, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-5 border-b border-white/5 last:border-0">
              <Field label={`Stap ${i + 1} — titel`} value={p.title} onChange={(v) => setItem("process", i, "title", v)} testid={`set-step-title-${i}`} />
              <Field label={`Stap ${i + 1} — tekst`} value={p.text} onChange={(v) => setItem("process", i, "text", v)} testid={`set-step-text-${i}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="bg-[#FF1A1A] text-white px-8 py-3 font-semibold flex items-center gap-2 hover:bg-[#E60000] transition-colors disabled:opacity-60">
          {saving ? <><Loader2 size={17} className="animate-spin" /> Opslaan...</> : <><Save size={17} /> Alle instellingen opslaan</>}
        </button>
      </div>
    </div>
  );
};

export default SettingsManager;
